/**
 * SpanViewer - Wrapper with Timeline/Tree toggle
 */

import { useState } from 'react';
import { useTraceSpans } from '../hooks/useTraces';
import { SpanTimeline } from './SpanTimeline';
import { SpanTree } from './SpanTree';
import { formatDuration } from './spanConstants';
import { useI18n } from '@/i18n';

type ViewMode = 'timeline' | 'tree';

interface SpanViewerProps {
  traceId: string;
  traceStartTime: string;
  traceDuration: number | null;
}

export function SpanViewer({ traceId, traceStartTime, traceDuration }: SpanViewerProps) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const [mode, setMode] = useState<ViewMode>('timeline');
  const { data, isLoading } = useTraceSpans(traceId, { size: 100 });

  if (isLoading) {
    return <div className="text-xs text-gray-500 py-2">{t.loadingSpans}</div>;
  }

  if (!data || data.content.length === 0) {
    return <div className="text-xs text-gray-400 py-2">{t.noSpans}</div>;
  }

  const truncated = data.total > data.content.length;

  return (
    <div>
      {/* Toggle + total duration */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex bg-gray-100 rounded-md p-0.5">
          <button
            onClick={() => setMode('timeline')}
            className={`px-2.5 py-1 text-xs rounded transition-colors ${
              mode === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.viewTimeline}
          </button>
          <button
            onClick={() => setMode('tree')}
            className={`px-2.5 py-1 text-xs rounded transition-colors ${
              mode === 'tree' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.viewTree}
          </button>
        </div>
        {traceDuration != null && (
          <span className="text-xs text-gray-500">
            {t.totalDuration.replace('{duration}', formatDuration(traceDuration))}
          </span>
        )}
      </div>

      {/* Content */}
      {mode === 'timeline' ? (
        <SpanTimeline
          spans={data.content}
          traceStartTime={traceStartTime}
          traceDuration={traceDuration}
          truncated={truncated}
          totalSpans={data.total}
        />
      ) : (
        <SpanTree traceId={traceId} />
      )}
    </div>
  );
}
